import { AlertCircle, Phone, DollarSign, FileText, CheckCircle, Clock } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const TaxDebtResolution = () => {
  return (
    <GenericPracticePage
      title="Tax Debt Resolution"
      subtitle="Overwhelming IRS or state tax debt? We negotiate settlements, payment plans, and penalty relief to resolve your tax problems and stop collection actions."
      icon={<AlertCircle className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">IRS Tax Debt Resolution</h2>
            <p className="text-lg text-muted-foreground mb-4">
              If you owe back taxes to the IRS or state, you're not alone. Millions of Americans struggle with tax debt. The IRS can garnish wages, levy bank accounts, and file liens against your property - but you have options. With experienced representation, you can resolve tax debt for less than you owe, set up affordable payment plans, or temporarily halt collections.
            </p>
            <p className="text-lg text-muted-foreground">
              Don't let tax debt control your life. We negotiate directly with the IRS and state agencies to find the best resolution for your situation.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Tax Debt Resolution Options</h2>
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <div className="flex items-start gap-4">
                  <DollarSign className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Offer in Compromise (OIC)</h3>
                    <p className="text-muted-foreground mb-3">
                      Settle your tax debt for less than the full amount owed. If you qualify, the IRS accepts a lump sum or payment plan as full satisfaction of your tax liability.
                    </p>
                    <div className="bg-accent/10 p-4 rounded">
                      <p className="text-sm font-semibold mb-2">Who Qualifies:</p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Doubt as to collectibility (can't pay full amount)</li>
                        <li>• Doubt as to liability (you don't actually owe it)</li>
                        <li>• Effective tax administration (paying would cause hardship)</li>
                        <li>• Must be current with filing all required returns</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <div className="flex items-start gap-4">
                  <FileText className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Installment Agreement</h3>
                    <p className="text-muted-foreground mb-3">
                      Pay your tax debt over time through monthly payments. Various installment agreement options available based on amount owed.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-accent/10 p-3 rounded">
                        <p className="font-semibold text-sm mb-1">Short-Term (≤180 days)</p>
                        <p className="text-xs text-muted-foreground">Pay within 6 months</p>
                      </div>
                      <div className="bg-accent/10 p-3 rounded">
                        <p className="font-semibold text-sm mb-1">Long-Term (&gt;180 days)</p>
                        <p className="text-xs text-muted-foreground">Monthly payments up to 72 months</p>
                      </div>
                      <div className="bg-accent/10 p-3 rounded">
                        <p className="font-semibold text-sm mb-1">Partial Payment (PPIA)</p>
                        <p className="text-xs text-muted-foreground">Pay less than full amount over time</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Penalty Abatement</h3>
                    <p className="text-muted-foreground mb-3">
                      The IRS can remove or reduce penalties if you have reasonable cause for not filing or paying on time. Penalties can add 25-50% to your tax bill.
                    </p>
                    <div className="bg-accent/10 p-4 rounded">
                      <p className="text-sm font-semibold mb-2">Common Grounds for Abatement:</p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• First-time penalty abatement (FTA)</li>
                        <li>• Reasonable cause (illness, death, disaster)</li>
                        <li>• Erroneous written advice from IRS</li>
                        <li>• Statutory exceptions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <div className="flex items-start gap-4">
                  <Clock className="h-10 w-10 text-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Currently Not Collectible (CNC)</h3>
                    <p className="text-muted-foreground mb-3">
                      Temporarily halt IRS collection when paying would prevent you from meeting basic living expenses. The IRS can't levy or garnish while in CNC status.
                    </p>
                    <div className="bg-accent/10 p-4 rounded">
                      <p className="text-sm font-semibold mb-2">Important Notes:</p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Debt continues to accrue interest</li>
                        <li>• Must provide detailed financial information</li>
                        <li>• Status reviewed periodically</li>
                        <li>• Gives time to improve financial situation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Stop IRS Collection Actions</h2>
            <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">We can stop or reverse aggressive IRS collection actions:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Wage Garnishment Release</p>
                    <p className="text-xs text-muted-foreground">Stop the IRS from taking up to 70% of your paycheck</p>
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Bank Levy Release</p>
                    <p className="text-xs text-muted-foreground">Get frozen funds released back to you</p>
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Federal Tax Lien Withdrawal</p>
                    <p className="text-xs text-muted-foreground">Remove liens destroying your credit</p>
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Asset Seizure Prevention</p>
                    <p className="text-xs text-muted-foreground">Stop IRS from seizing property, cars, or other assets</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Our Tax Debt Resolution Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Financial Analysis</h3>
                  <p className="text-sm text-muted-foreground">Complete review of your income, expenses, assets, and liabilities to determine the best resolution strategy</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Stop Collections</h3>
                  <p className="text-sm text-muted-foreground">If you're facing immediate collection action, we act fast to protect your assets and income</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Negotiate Solution</h3>
                  <p className="text-sm text-muted-foreground">We prepare and submit applications for OIC, installment agreements, penalty abatement, or CNC status</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Final Resolution</h3>
                  <p className="text-sm text-muted-foreground">Secure the best possible resolution - often settling for pennies on the dollar or affordable monthly payments</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Don't Let Tax Debt Destroy Your Life</h2>
            <p className="text-muted-foreground mb-4">
              Tax debt doesn't go away. Ignoring it only makes things worse as interest and penalties pile up and the IRS takes aggressive collection action. But you don't have to face this alone.
            </p>
            <p className="text-muted-foreground">
              We've helped thousands of taxpayers resolve IRS debt and get back on track financially. Let us analyze your situation and find the best path forward.
            </p>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Drowning in Tax Debt?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today for a confidential consultation. Find out how we can resolve your tax debt and stop IRS collections.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default TaxDebtResolution;
